import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  Suspense,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import {
  Link,
  NavLink,
  Outlet,
  ScrollRestoration,
  useLocation,
  useNavigate,
  type Location,
} from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CirclePlay,
  Coins as CoinsIcon,
  FileText,
  Gamepad2,
  Gift,
  Languages,
  LifeBuoy,
  LogOut,
  Mail,
  MailWarning,
  Megaphone,
  ShieldCheck,
  Trophy,
  UserCog,
  UserPen,
  Wallet,
  X,
} from 'lucide-react';
import { api, fmtCoins, type Me } from '../lib/api';
import { useMe } from '../lib/session';
import { dailyQuery, statsQuery } from '../lib/queries';
import { LiveFeed } from './LiveFeed';
import { AuthProvider, useAuthDialog, useMedia, useStandalone } from './AuthDialog';
import { CoinGlyph, CoinMascot, Swatch } from './Coin';
import { Avatar } from './Avatar';
import { useModal } from './modal';
import { isAppMode } from '../lib/appMode';
import { hideLaunchSplash, KEEPS_SPLASH } from '../lib/splash';
import { APP_BONUS_EVENT, LAUNCH_EVENT, syncAppDevice } from '../lib/appDevice';
import { stopBrowserPush, syncBrowserPush } from '../lib/notify';
import { NotifyAskProvider } from './NotifyAsk';
import { GetAppBanner } from './GetApp';
import { LocaleFlag } from './LocaleFlag';
import { lazyComponent } from '../lib/lazy';
import { AVAILABLE, LOCALE_INFO, T, fmtNumber, gap, msg, t, tc, useLocale } from '../i18n';

/* ------------------------------------------------------------------ Screens */

// Screen names are marked with msg() (the English stays in these tables: screenFor returns it, and
// lib/seo.ts writes the <title> from it) and shown with t(). Their notes are on ROOTS and STACK.

/** Desktop navigation (the segmented control in the floating header). */
const nav: { to: string; label: string; icon: typeof Wallet }[] = [
  { to: '/earn', label: msg('Earn'), icon: Gamepad2 },
  { to: '/cashout', label: msg('Cash out'), icon: CoinsIcon },
  { to: '/rewards', label: msg('Rewards'), icon: Gift },
  { to: '/leaderboard', label: msg('Leaderboard'), icon: Trophy },
  { to: '/support', label: msg('Help'), icon: LifeBuoy },
];

/** Phone tab bar: Cash out sits in the middle as the raised coin, the one money action. */
const TABS: { to: string; label: string; icon?: typeof Wallet }[] = [
  { to: '/earn', label: msg('Earn'), icon: Gamepad2 },
  { to: '/rewards', label: msg('Rewards'), icon: Gift },
  { to: '/cashout', label: msg('Cash out') },
  { to: '/leaderboard', label: msg('Leaders'), icon: Trophy },
  { to: '/support', label: msg('Help'), icon: LifeBuoy },
];

/** How each route sits in the app: tab roots (depth 0) or stack screens with a back button. */
interface Screen {
  title: string;
  /** The app bar's title on phones under 360px wide, where the full one would crowd the balance. */
  short?: string | undefined;
  depth: 0 | 1;
  /** Where Back goes when there is no in-app history (a deep link or a fresh launch). */
  parent: string;
  /** False on screens that dock their own bar to the bottom edge (a chat's composer). */
  tabBar: boolean;
}
const ROOTS: Record<string, [title: string, short?: string]> = {
  // i18n: name of the Earn screen, where people open offer walls to earn coins (tab bar, menus, screen title): keep it short
  '/earn': [msg('Earn')],
  // i18n: name of the Cash out screen, where coins become gift cards (tab bar, menus, screen title): keep it short
  '/cashout': [msg('Cash out')],
  // i18n: name of the Rewards screen: daily bonus, levels and referrals (tab bar, menus, screen title): keep it short
  '/rewards': [msg('Rewards')],
  '/leaderboard': [
    // i18n: name of the Leaderboard screen, the ranking of top earners (menus, screen title)
    msg('Leaderboard'),
    // i18n: short name of the Leaderboard screen, for the tab bar and narrow phones: keep it very short
    msg('Leaders'),
  ],
  // i18n: name of the Help screen: answers and conversations with support (tab bar, menus, screen title): keep it short
  '/support': [msg('Help')],
};
const STACK: [RegExp, string, string, { tabBar?: false }?][] = [
  // i18n: screen title: the user's coin balance and its history
  [/^\/wallet\/?$/, msg('Wallet'), '/earn'],
  // i18n: screen title: the user's account settings
  [/^\/account\/?$/, msg('Account'), '/earn'],
  // i18n: screen title: one conversation with support
  [/^\/support\/[^/]+\/?$/, msg('Conversation'), '/support', { tabBar: false }],
  [/^\/terms\/?$/, msg('Terms of Service'), '/earn'],
  [/^\/privacy\/?$/, msg('Privacy Policy'), '/earn'],
  // i18n: screen title: confirming the user's email address with the link we emailed
  [/^\/verify-email\/?$/, msg('Verify email'), '/earn'],
  // i18n: screen title: choosing a new password after forgetting the old one
  [/^\/reset-password\/?$/, msg('New password'), '/earn'],
  // The public guide pages (pre-rendered for search), next to the screen each one explains.
  // i18n: title of the guide page that explains how GaintPlay works
  [/^\/how-it-works\/?$/, msg('How it works'), '/earn'],
  // i18n: title of the guide page about the kinds of offers (games, surveys, apps) that pay coins
  [/^\/ways-to-earn\/?$/, msg('Ways to earn'), '/earn'],
  // i18n: title of the guide page about the gift cards people can cash out to
  [/^\/gift-cards\/?$/, msg('Gift cards'), '/cashout'],
  // i18n: frequently asked questions: your language's usual short name for them
  [/^\/faq\/?$/, msg('FAQ'), '/support'],
];
export function screenFor(pathname: string): Screen {
  const root = ROOTS[pathname.replace(/\/$/, '') || '/'];
  if (root) return { title: root[0], short: root[1], depth: 0, parent: '/earn', tabBar: true };
  for (const [re, title, parent, opts] of STACK)
    if (re.test(pathname)) return { title, depth: 1, parent, tabBar: opts?.tabBar ?? true };
  // Anything else (a 404, a referral link) behaves like a pushed screen that leads home.
  return { title: '', depth: 1, parent: '/earn', tabBar: true };
}

/** Kind of screen change, read by the view-transition CSS in index.css (html[data-nav]). */
export function navKind(
  from: string,
  to: string,
  action: 'PUSH' | 'POP' | 'REPLACE',
  state?: unknown,
): 'tab' | 'push' | 'pop' {
  if ((state as { nav?: string } | null)?.nav === 'pop') return 'pop';
  const a = screenFor(from).depth;
  const b = screenFor(to).depth;
  if (action === 'POP') return a > b || (a === b && a > 0) ? 'pop' : 'tab';
  return b > a || (a === b && a > 0) ? 'push' : 'tab';
}

const DESKTOP = '(min-width: 1024px)';
const subscribeDesktop = (cb: () => void) => {
  const m = window.matchMedia(DESKTOP);
  m.addEventListener('change', cb);
  return () => m.removeEventListener('change', cb);
};
/** True at lg (1024px) and up: the desktop site. Below it, signed-in people get the app. The
 *  pre-render (no window) gets false, the markup every width starts from. */
export function useIsDesktop() {
  return useSyncExternalStore(
    subscribeDesktop,
    () => window.matchMedia(DESKTOP).matches,
    () => false,
  );
}

/** Full-bleed routes manage their own width (marketing sections). */
const FULL_BLEED = ['/', '/signup', '/login', '/forgot-password'];
/** The installed app's front door (pages/Auth.tsx): a full-screen welcome with no site chrome. */
const WELCOME = ['/signup', '/login', '/forgot-password'];
/** Pages the new-member guide never covers: links from emails, and the legal texts. */
const NO_GUIDE = ['/verify-email', '/reset-password', '/unsubscribe', '/terms', '/privacy'];
/** The walkthrough every new account gets once, and anyone can open from the footer's "Guide me"
 *  (components/Guide.tsx): loaded only when it shows. */
const Guide = lazyComponent(() => import('./Guide').then((m) => m.Guide));

interface Daily {
  day: number;
  claimedToday: boolean;
  coins: number;
}

/** Balance for the app bar chip: exact up to 999,999, then "1.48M" (the reader's short form: "1,48
 *  Mio.", "148万") so the bar never overflows. */
const compact = (n: number) =>
  n >= 1_000_000
    ? fmtNumber(n, { notation: 'compact', maximumFractionDigits: n >= 10_000_000 ? 1 : 2 })
    : fmtCoins(n);

export function Logo({ invert = false, to = '/' }: { invert?: boolean; to?: string }) {
  return (
    <Link
      to={to}
      className="flex min-h-10 shrink-0 items-center gap-2 rounded-[10px]"
      // i18n: label of the GaintPlay logo, a link to the home page
      aria-label={t('GaintPlay home')}
    >
      <img src="/brand/logo-48.png" alt="" width={32} height={32} className="h-8 w-8" />
      <span
        className={`text-[19px] font-extrabold tracking-[-0.03em] ${invert ? 'text-white' : 'text-ink'}`}
      >
        GaintPlay
      </span>
    </Link>
  );
}

export function Layout() {
  return (
    <AuthProvider>
      <NotifyAskProvider>
        <Shell />
      </NotifyAskProvider>
    </AuthProvider>
  );
}

/**
 * Which saved scroll position a location gets back. In-app navigations: one per history entry (the
 * router's default). Every page load from outside the app's history (a typed URL, a link, a reload)
 * has the key "default", so keyed by path instead: a reload comes back to its place, but a page never
 * opens at another page's old scroll position (it used to be clamped away while screens loaded
 * slowly; now that they render at once it would show).
 */
const scrollKey = (location: Location) =>
  location.key === 'default' ? `default:${location.pathname}` : location.key;

/** Two flags from 1px sentinels at the top of the page (no scroll listener): `scrolled` once the
 *  page moves at all (the bar's hairline appears), `titled` once the large title has slid under the
 *  bar (the bar shows the screen name instead of the wordmark). */
function useScrollFlags() {
  const top = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLDivElement>(null);
  const [flags, setFlags] = useState({ scrolled: false, titled: false });
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      setFlags((f) => {
        const next = { ...f };
        for (const e of entries) {
          if (e.target === top.current) next.scrolled = !e.isIntersecting;
          if (e.target === title.current) next.titled = !e.isIntersecting;
        }
        return next.scrolled === f.scrolled && next.titled === f.titled ? f : next;
      });
    });
    if (top.current) io.observe(top.current);
    if (title.current) io.observe(title.current);
    return () => io.disconnect();
  }, []);
  const sentinels = (
    <>
      <div
        ref={top}
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-0 h-1 w-px"
      />
      <div
        ref={title}
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-[calc(var(--safe-top)+64px)] h-px w-px"
      />
    </>
  );
  return { ...flags, sentinels };
}

function Shell() {
  const { data: me, isPending } = useMe();
  const { pathname } = useLocation();
  const desktop = useIsDesktop();
  // Launched as the installed app (the Play Store TWA, a home-screen app) on a phone or tablet.
  const installed = useStandalone() && !desktop;
  const { scrolled, titled, sentinels } = useScrollFlags();
  const [account, setAccount] = useState(false);
  // The walkthrough opened from the footer ("Guide me"), any time, signed in or not.
  const [tour, setTour] = useState(false);
  const fullBleed = FULL_BLEED.includes(pathname) && !me;
  const signedIn = !!me;
  const screen = screenFor(pathname);
  // The installed app's welcome screen owns the window: just the page, no header, live band or
  // footer. It stays bare while a fresh sign-in leaves for the app, so no chrome flashes on the way.
  const welcome = installed && WELCOME.includes(pathname.replace(/(.)\/$/, '$1'));

  // The Android app's device data (src/launch.ts) goes to the API once someone is signed in, and
  // again when it changes or another account signs in on the phone.
  const userId = me?.user.id;
  useEffect(() => {
    if (!userId) return;
    void syncAppDevice(userId);
    // A browser that allows notifications keeps its subscription current (lib/notify.ts).
    void syncBrowserPush();
    // launch.ts may finish after this page started (rarely): send as soon as it has.
    const again = () => void syncAppDevice(userId);
    window.addEventListener(LAUNCH_EVENT, again);
    return () => window.removeEventListener(LAUNCH_EVENT, again);
  }, [userId]);

  // The installed app's launch splash (index.html, lib/splash.ts) comes down once the first screen
  // is ready: here as soon as the session is known, except where the screen sees to it itself.
  useEffect(() => {
    if (isPending || KEEPS_SPLASH.has(pathname.replace(/(.)\/$/, '$1'))) return;
    hideLaunchSplash();
  }, [isPending, pathname]);

  // <html data-app>: turns on app mode below lg (tokens and the `app:` variant in index.css).
  useLayoutEffect(() => {
    document.documentElement.toggleAttribute('data-app', signedIn);
    if (!signedIn) setAccount(false);
  }, [signedIn]);
  // <html data-tabless>: this screen docks its own bar to the bottom edge instead of the tab bar
  // (a chat's composer), so --app-bottom is just the safe area (index.css).
  useLayoutEffect(() => {
    document.documentElement.toggleAttribute('data-tabless', signedIn && !screen.tabBar);
  }, [signedIn, screen.tabBar]);
  // <html data-keyboard> while the on-screen keyboard is up: the tab bar steps aside.
  useKeyboardFlag(signedIn && !desktop);

  // In app mode, browsers without View Transitions still get a short settle-in when the screen
  // changes. The website (desktop, or signed out) changes pages instantly.
  const mainRef = useRef<HTMLElement>(null);
  const firstPath = useRef(pathname);
  useLayoutEffect(() => {
    if (pathname === firstPath.current) return;
    firstPath.current = pathname;
    if ('startViewTransition' in document || !isAppMode()) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    mainRef.current?.animate(
      [
        { opacity: 0, transform: 'translateY(6px)' },
        { opacity: 1, transform: 'none' },
      ],
      { duration: 200, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
    );
  }, [pathname]);

  return (
    // overflow-x: clip is the safety net (no page can scroll sideways); unlike `hidden` it
    // doesn't create a scroll container, so the sticky bars keep working.
    <div className="relative flex min-h-dvh flex-col overflow-x-clip">
      {/* New pages open at the top (and /#anchors at their section), not at the old scroll spot. */}
      <ScrollRestoration getKey={scrollKey} />
      {sentinels}
      {me && !welcome && (
        <AppBar me={me} scrolled={scrolled} titled={titled} onAccount={() => setAccount(true)} />
      )}
      {/* Signed out in the installed app, the mark leads to its front door, not the website's home. */}
      {!welcome && (
        <Header scrolled={scrolled} pending={isPending} home={installed && !me ? '/signup' : '/'} />
      )}
      {/* The live band is the website's: desktop, and signed-out phones in a browser. */}
      {!welcome && (desktop || (!me && !installed)) && <LiveFeed />}
      {!welcome && <Announcement />}
      {!welcome && <GetAppBanner />}
      {me && !welcome && !me.user.emailVerifiedAt && <VerifyBanner />}
      <main
        ref={mainRef}
        className={
          fullBleed || welcome
            ? 'flex-1'
            : me
              ? 'mx-auto w-full max-w-[1200px] flex-1 px-(--gutter) pb-[calc(var(--app-bottom)+32px)] pt-4 lg:pb-24 lg:pt-10'
              : 'mx-auto w-full max-w-[1200px] flex-1 px-(--gutter) pb-20 pt-6 md:pt-10 lg:pb-24'
        }
      >
        <Outlet />
      </main>
      {!welcome && <Footer onGuide={() => setTour(true)} />}
      {me && !welcome && screen.tabBar && <TabBar />}
      {me && account && (
        <AccountSheet
          me={me}
          onClose={() => setAccount(false)}
          onGuide={() => {
            setAccount(false);
            setTour(true);
          }}
        />
      )}
      {me?.user.guideDoneAt === null && !welcome && !NO_GUIDE.includes(pathname) ? (
        <Suspense fallback={null}>
          <Guide />
        </Suspense>
      ) : (
        tour && (
          <Suspense fallback={null}>
            <Guide onClose={() => setTour(false)} />
          </Suspense>
        )
      )}
      <AppBonusSticker />
    </div>
  );
}

/* ------------------------------------------------------------------ App bar (phones, signed in) */

/** Docked, full-bleed top bar. Tab roots: mark + wordmark (the screen name once the page's large
 *  title scrolls under it), balance chip, avatar. Stack screens: back + title, like a native stack. */
function AppBar({
  me,
  scrolled,
  titled,
  onAccount,
}: {
  me: Me;
  scrolled: boolean;
  titled: boolean;
  onAccount: () => void;
}) {
  const { pathname } = useLocation();
  const screen = screenFor(pathname);
  const stack = screen.depth > 0;
  const title = (
    <span className="min-w-0 truncate text-[18px] font-bold tracking-[-0.015em]">
      {screen.short ? (
        <>
          <span className="max-[360px]:hidden">{t(screen.title)}</span>
          <span className="min-[360px]:hidden">{t(screen.short)}</span>
        </>
      ) : (
        t(screen.title)
      )}
    </span>
  );
  return (
    <header
      className={`app-bar pt-safe sticky top-0 z-30 border-b bg-canvas transition-[border-color] duration-200 lg:hidden ${scrolled ? 'border-hairline' : 'border-transparent'}`}
    >
      <div className="app-bar-row flex h-[var(--app-bar-h)] items-center gap-1 pl-[max(12px,var(--safe-left))] pr-[max(14px,var(--safe-right))]">
        {stack ? (
          <BackButton parent={screen.parent} />
        ) : (
          <Link
            to="/earn"
            aria-label={t('GaintPlay home')}
            className="pressable grid h-11 w-11 shrink-0 place-items-center rounded-full"
          >
            <img src="/brand/logo-48.png" alt="" width={32} height={32} className="h-8 w-8" />
          </Link>
        )}
        <div className="relative h-full min-w-0 flex-1">
          {stack ? (
            <span className="absolute inset-0 flex items-center">{title}</span>
          ) : (
            <>
              <span
                aria-hidden="true"
                className={`absolute inset-0 flex items-center text-[20px] font-extrabold tracking-[-0.03em] transition duration-200 max-[360px]:hidden ${titled ? '-translate-y-1.5 opacity-0' : ''}`}
              >
                GaintPlay
              </span>
              <span
                className={`absolute inset-0 flex items-center transition duration-200 ${titled ? '' : 'min-[360px]:translate-y-1.5 min-[360px]:opacity-0'}`}
              >
                {title}
              </span>
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {pathname !== '/wallet' && <BalanceChip me={me} />}
          {!stack && (
            <button
              type="button"
              onClick={onAccount}
              aria-haspopup="dialog"
              // i18n: the avatar button that opens the account sheet; {name} is the user's name
              aria-label={t('Account for {name}', {
                name: me.user.displayName ?? me.user.username,
              })}
              className="pressable grid h-11 w-11 place-items-center rounded-full"
            >
              <Avatar name={me.user.username} size={34} className="border-[1.5px] border-ink" />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function BackButton({ parent }: { parent: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  return (
    <button
      type="button"
      // i18n: the app bar's back button (to the previous screen)
      aria-label={t('Back')}
      onClick={() => {
        // In-app history: a real back (Android's back button does the same). A deep link or a
        // fresh launch has nothing behind it, so go up to the parent screen instead of leaving.
        if (location.key !== 'default') void navigate(-1);
        else void navigate(parent, { replace: true, state: { nav: 'pop' } });
      }}
      className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-ink active:bg-ink/[0.06]"
    >
      <ChevronLeft size={26} strokeWidth={2} aria-hidden="true" />
    </button>
  );
}

function BalanceChip({ me }: { me: Me }) {
  const n = me.balance.available;
  // i18n: the balance chip in the app bar, a link to the wallet (its tooltip, and what screen readers say)
  const label = t('{n, plural, one {# coin} other {# coins}}: open your wallet', { n });
  return (
    <Link
      to="/wallet"
      title={label}
      className="pressable flex h-9 items-center gap-1.5 rounded-full border border-hairline bg-paper pl-1.5 pr-3 active:bg-coin-tint"
    >
      <CoinGlyph className="h-[22px] w-[22px]" />
      <span className="whitespace-nowrap text-[15px] font-extrabold tabular-nums tracking-[-0.01em] text-ink">
        <span aria-hidden="true">{compact(n)}</span>
        <span className="sr-only">{label}</span>
      </span>
    </Link>
  );
}

/* ------------------------------------------------------------------ Tab bar (phones, signed in) */

/** Docked to the bottom edge above the gesture bar. Material-style active pill that grows in; Cash
 *  out is the raised orange coin in the middle (the brand's sticker button, pressed in when active). */
function TabBar() {
  return (
    <nav
      // i18n: name of the main navigation, for screen readers ("Main navigation")
      aria-label={t('Main')}
      onPointerDown={(e) => {
        if (e.pointerType === 'touch' && (e.target as Element).closest('a')) navigator.vibrate?.(6);
      }}
      className="tab-bar pb-safe fixed inset-x-0 bottom-0 z-40 border-t border-hairline bg-paper pl-[var(--safe-left)] pr-[var(--safe-right)] lg:hidden"
    >
      <div className="mx-auto grid h-[var(--tab-bar-h)] max-w-[560px] grid-cols-5">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            className="group relative flex min-w-0 flex-col items-center justify-center gap-1 rounded-[14px] outline-offset-[-3px]"
          >
            {({ isActive }) =>
              tab.icon ? (
                <>
                  <span className="relative grid h-8 w-14 place-items-center transition-transform duration-100 ease-out group-active:scale-90">
                    <span
                      aria-hidden="true"
                      className={`absolute inset-0 rounded-full ${isActive ? 'animate-[pill-in_300ms_var(--ease-out)] bg-ink' : 'group-active:bg-ink/[0.07]'}`}
                    />
                    <tab.icon
                      size={22}
                      strokeWidth={isActive ? 2.1 : 1.85}
                      aria-hidden="true"
                      className={`relative ${isActive ? 'text-white' : 'text-graphite'}`}
                    />
                  </span>
                  <TabLabel active={isActive}>{t(tab.label)}</TabLabel>
                </>
              ) : (
                <>
                  <span
                    aria-hidden="true"
                    className={`absolute left-[calc(50%-27px)] top-[-18px] grid h-[54px] w-[54px] place-items-center rounded-full border-[1.5px] border-ink bg-coin transition-[transform,box-shadow] duration-100 ease-out group-active:translate-x-[2px] group-active:translate-y-[2px] group-active:shadow-[1px_1px_0_var(--color-ink)] ${isActive ? 'translate-x-[1.5px] translate-y-[1.5px] shadow-[1.5px_1.5px_0_var(--color-ink)]' : 'shadow-[3px_3px_0_var(--color-ink)]'}`}
                  >
                    <svg viewBox="0 0 100 100" className="h-full w-full">
                      <circle
                        cx="50"
                        cy="50"
                        r="37"
                        fill="none"
                        stroke="#111110"
                        strokeWidth="2.5"
                        opacity="0.3"
                      />
                      <path
                        d="M12.6 7.2A3.6 3.6 0 1 0 13.6 10h-3.2"
                        transform="translate(18 18) scale(3.2)"
                        fill="none"
                        stroke="#111110"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                  <span className="h-8" aria-hidden="true" />
                  <TabLabel active={isActive}>{t(tab.label)}</TabLabel>
                </>
              )
            }
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function TabLabel({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <span
      className={`max-w-full truncate px-0.5 text-[12px] leading-none ${active ? 'font-bold text-ink' : 'font-semibold text-stone'}`}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ On-screen keyboard */

/** Fields that bring up the on-screen keyboard. */
function typesText(el: EventTarget | null): boolean {
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement)
    return (
      /^(text|search|email|url|tel|password|number)$/.test(el.type) &&
      !el.readOnly &&
      !el.disabled &&
      el.inputMode !== 'none'
    );
  return el instanceof HTMLElement && el.isContentEditable;
}

/** More than a browser's collapsing URL bar, less than any phone keyboard. */
const KEYBOARD_MIN = 120;

/**
 * Sets <html data-keyboard> while the on-screen keyboard is up, for index.css: the tab bar steps
 * aside (the page resizes for the keyboard, interactive-widget=resizes-content, so it would ride on
 * top of it) and --app-bottom drops to the safe area. Up means a text field has focus and the
 * viewport is at least KEYBOARD_MIN shorter than without one. The attribute changes inside the focus
 * and resize events, before the browser paints the resized page, so the bar never shows over the
 * keyboard for a frame. And:
 *  - tapping a field hides the bar at once, as the keyboard starts to rise. A hardware keyboard
 *    never shrinks the page: the bar comes back after a moment, and the guess stops for the session;
 *  - focus moving from one field to the next keeps it hidden, with nothing in between;
 *  - it comes back once the page is full height again: after a blur, or when Android's back key
 *    puts the keyboard away and the field keeps focus.
 */
function useKeyboardFlag(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    const root = document.documentElement;
    const vv = window.visualViewport;
    // In layout pixels, so pinch zoom doesn't read as a keyboard.
    const height = () => (vv ? vv.height * vv.scale : window.innerHeight);
    let width = window.innerWidth;
    let full = height();
    let tappedAt = -Infinity;
    let blurredAt = -Infinity;
    let expectUntil = 0; // a field was just tapped: the keyboard is on its way until then
    let guess = true; // false once a tapped field brought no keyboard (a hardware one)
    let timer = 0;
    const update = () => {
      window.clearTimeout(timer);
      const now = performance.now();
      const h = height();
      const typing = typesText(document.activeElement);
      if (window.innerWidth !== width) {
        width = window.innerWidth; // rotated: a new baseline
        full = h;
      }
      // The tallest the page has been, re-measured whenever nothing's being typed (split screen,
      // a URL bar that settled), but not while a keyboard may still be closing after a blur.
      if (h > full || (!typing && now - blurredAt > 1000)) full = h;
      const up = full - h >= KEYBOARD_MIN;
      if (expectUntil && (up || !typing)) expectUntil = 0;
      else if (expectUntil && now >= expectUntil) {
        expectUntil = 0;
        guess = false;
      }
      const hide = (typing && (up || expectUntil > 0)) || (up && now - blurredAt < 1000);
      if (root.hasAttribute('data-keyboard') !== hide) root.toggleAttribute('data-keyboard', hide);
      // Settle without waiting for another event once the guess or the blur window runs out.
      const deadline = expectUntil || (hide && !typing ? blurredAt + 1000 : 0);
      if (deadline) timer = window.setTimeout(update, deadline - now + 20);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') tappedAt = performance.now();
    };
    const onFocusIn = (e: FocusEvent) => {
      if (guess && typesText(e.target) && performance.now() - tappedAt < 800)
        expectUntil = performance.now() + 1000;
      update();
    };
    const onFocusOut = (e: FocusEvent) => {
      // Straight on to another field (the keyboard's Next, a tap on the next one): no change.
      if (typesText(e.relatedTarget)) return;
      blurredAt = performance.now();
      // A programmatic move can land on a field a moment later; decide after it.
      window.clearTimeout(timer);
      timer = window.setTimeout(update, 60);
    };
    vv?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    document.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);
    update();
    return () => {
      window.clearTimeout(timer);
      vv?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
      document.removeEventListener('pointerdown', onPointerDown, { capture: true });
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      root.removeAttribute('data-keyboard');
    };
  }, [enabled]);
}

/* ------------------------------------------------------------------ Bottom sheet */

/** What a sheet's content can do with the sheet around it: `useSheet()`, or `children` as a
 *  function. */
export interface SheetControls {
  /** Plays the exit animation, then calls the sheet's onClose: the content's own way out (a Done
   *  button, a picked option). It isn't a dismissal, so it works while the sheet is `locked`. */
  close: () => void;
}
const SheetContext = createContext<SheetControls | null>(null);
/** The BottomSheet this component sits in (body, header or footer); null outside a sheet. */
export const useSheet = () => useContext(SheetContext);

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
/** How far a locked sheet gives under a downward drag: a short, stiffening rubber-band (max 40px). */
const rubberBand = (dy: number) => 40 * (1 - 1 / (dy / 70 + 1));

/**
 * Native bottom sheet: scrim, drag handle, slide up; closes by tapping the scrim or handle, dragging
 * it down, Escape, or Android's back button (the dialog stack in useModal: one close watcher per
 * dialog, so back closes the top sheet only). Focus is trapped while open and returns to the
 * opener. Render it while open; it plays its exit animation and then calls onClose.
 *
 * - `header` sits in the draggable zone above the scrolling body.
 * - `footer` is pinned under the body, above the safe area, while the body scrolls (actions).
 * - `locked` holds the sheet open (a request in flight): drag only rubber-bands, and the scrim,
 *   handle, Escape and back give it a small nudge instead of closing it.
 * - Content closes the sheet with its exit animation through `useSheet().close()`, or
 *   `children` as a function: `{({ close }) => …}`.
 */
export function BottomSheet({
  onClose,
  label,
  labelledBy,
  header,
  footer,
  locked = false,
  children,
}: {
  onClose: () => void;
  label?: string;
  labelledBy?: string;
  header?: ReactNode;
  footer?: ReactNode;
  locked?: boolean;
  children: ReactNode | ((sheet: SheetControls) => ReactNode);
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);
  const done = useRef(false);
  const onCloseRef = useRef(onClose);
  const lockedRef = useRef(locked);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
    lockedRef.current = locked;
  });
  const close = useCallback(() => {
    if (done.current) return;
    done.current = true;
    if (reducedMotion()) return onCloseRef.current();
    setClosing(true);
    window.setTimeout(() => onCloseRef.current(), 220);
  }, []);
  // Every way people dismiss the sheet comes through here. Locked: the sheet dips a little and
  // settles back, so the tap or the back press visibly registered but changed nothing.
  const dismiss = useCallback(() => {
    if (!lockedRef.current) return close();
    if (reducedMotion()) return;
    panel.current?.animate(
      [{ transform: 'none' }, { transform: 'translateY(10px)' }, { transform: 'none' }],
      { duration: 280, easing: 'cubic-bezier(0.2, 0, 0, 1)' },
    );
  }, [close]);
  useModal(panel, dismiss, undefined, { locked });
  const controls = useMemo<SheetControls>(() => ({ close }), [close]);

  // Drag to dismiss: the handle and header follow the finger; past ~30% or a flick closes.
  const drag = useRef<{ y: number; t: number; h: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  const onDown = (e: ReactPointerEvent) => {
    const el = panel.current;
    if (!el || closing || (e.pointerType === 'mouse' && e.button !== 0)) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { y: e.clientY, t: e.timeStamp, h: el.offsetHeight, moved: false };
    el.style.transition = 'none';
  };
  const onMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    const el = panel.current;
    if (!d || !el) return;
    const dy = e.clientY - d.y;
    if (Math.abs(dy) > 4) d.moved = true;
    // Down follows the finger (locked: a short rubber-band); up always resists.
    const y = dy < 0 ? dy / 5 : lockedRef.current ? rubberBand(dy) : dy;
    el.style.transform = `translateY(${y}px)`;
  };
  const onUp = (e: ReactPointerEvent) => {
    const d = drag.current;
    const el = panel.current;
    drag.current = null;
    if (!d || !el) return;
    suppressClick.current = d.moved;
    const dy = e.clientY - d.y;
    const speed = dy / Math.max(1, e.timeStamp - d.t);
    if (!lockedRef.current && (dy > d.h * 0.3 || (dy > 32 && speed > 0.5))) return close();
    el.style.transition = 'transform 240ms var(--ease-out)';
    el.style.transform = '';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" role="presentation">
      <div
        aria-hidden="true"
        onClick={dismiss}
        className="absolute inset-0 bg-ink/45"
        style={{
          animation: closing ? 'fade-out 220ms ease-in forwards' : 'fade-in 260ms ease-out',
        }}
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className="relative flex max-h-[calc(100dvh-var(--safe-top)-12px)] w-full max-w-[560px] flex-col rounded-t-[24px] border-x border-t border-hairline bg-canvas outline-none"
        style={{
          animation: closing
            ? 'sheet-down 220ms cubic-bezier(0.4, 0, 1, 1) forwards'
            : 'sheet-up 360ms var(--ease-stack) backwards',
        }}
      >
        <SheetContext.Provider value={controls}>
          <div
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            className="shrink-0 cursor-grab touch-none select-none active:cursor-grabbing"
          >
            {/* The pill's row stays 28px, so headers keep their place; the button's touch target
                is 44px, reaching 16px above the sheet's top edge (where a tap closes it anyway). */}
            <button
              type="button"
              // i18n: the drag handle at the top of a bottom sheet, which closes it
              aria-label={t('Close')}
              data-sheet-close=""
              aria-disabled={locked || undefined}
              onClick={() => {
                if (suppressClick.current) suppressClick.current = false;
                else dismiss();
              }}
              className="relative flex h-7 w-full items-center justify-center rounded-t-[24px] outline-offset-[-4px] before:absolute before:inset-x-0 before:-top-4 before:h-11 before:content-['']"
            >
              <span aria-hidden="true" className="h-1 w-10 rounded-full bg-ink/20" />
            </button>
            {header}
          </div>
          <div
            className={`min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 ${footer ? 'pb-4' : 'pb-[calc(20px+var(--safe-bottom))]'}`}
          >
            {typeof children === 'function' ? children(controls) : children}
          </div>
          {footer && (
            <div className="shrink-0 border-t border-hairline bg-paper px-4 pb-[calc(12px+var(--safe-bottom))] pt-3">
              {footer}
            </div>
          )}
        </SheetContext.Provider>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Account sheet (phones) */

function AccountSheet({
  me,
  onClose,
  onGuide,
}: {
  me: Me;
  onClose: () => void;
  /** Opens the walkthrough again (the app has no footer with "Guide me"). */
  onGuide: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const auth = useAuthDialog();
  const name = me.user.displayName ?? me.user.username;
  const logout = async () => {
    // This browser stops getting the account's notifications (before the session goes).
    await stopBrowserPush();
    await api('/auth/logout', { body: {} }).catch(() => {});
    qc.clear();
    onClose();
    void navigate('/');
  };
  const row =
    'press-row flex min-h-[54px] w-full items-center gap-3.5 px-4 text-left text-[16px] font-semibold text-ink outline-offset-[-3px]';
  const icon = {
    size: 20,
    strokeWidth: 1.75,
    'aria-hidden': true,
    className: 'shrink-0 text-graphite',
  } as const;
  const chevron = (
    <ChevronRight size={18} aria-hidden="true" className="ml-auto shrink-0 text-stone" />
  );
  const group =
    'overflow-hidden rounded-[16px] border border-hairline bg-paper divide-y divide-hairline';
  return (
    <BottomSheet
      onClose={onClose}
      labelledBy="account-sheet-title"
      header={
        <div className="flex items-center gap-3.5 px-5 pb-4 pt-1">
          <Avatar name={me.user.username} size={52} className="border-[1.5px] border-ink" />
          <div className="min-w-0 flex-1">
            <h2
              id="account-sheet-title"
              className="truncate text-[19px] font-bold leading-tight tracking-[-0.015em]"
            >
              {name}
            </h2>
            <p className="truncate text-[14px] text-stone">{me.user.email}</p>
            <div className="mt-2 flex flex-wrap gap-1.5 text-[12px] font-semibold text-graphite">
              <span className="rounded-full bg-paper px-2.5 py-1">
                {
                  // i18n: the user's level, a small badge (levels go up as they earn)
                  t('Level {level}', { level: me.user.level })
                }
              </span>
              <span className="rounded-full bg-paper px-2.5 py-1">
                {
                  // i18n: a small badge with the user's referral code (like "Code AB12CD"): keep it short
                  <T
                    msg="Code <b>{code}</b>"
                    values={{ code: me.user.referralCode }}
                    tags={{
                      b: (c) => <span className="font-bold tracking-wide text-ink">{c}</span>,
                    }}
                  />
                }
              </span>
            </div>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <div className={group}>
          <Link to="/wallet" onClick={onClose} className={row}>
            <Wallet {...icon} /> {t('Wallet')}
            <span className="ml-auto flex shrink-0 items-center gap-1 text-[14px] font-bold tabular-nums text-graphite">
              <CoinGlyph /> {fmtCoins(me.balance.available)}
            </span>
            <ChevronRight size={18} aria-hidden="true" className="shrink-0 text-stone" />
          </Link>
          <Link to="/account" onClick={onClose} className={row}>
            <UserCog {...icon} />{' '}
            {
              // i18n: menu item: the Account screen (settings, privacy, deleting the account)
              t('Account & privacy')
            }{' '}
            {chevron}
          </Link>
          <Link to="/rewards" onClick={onClose} className={row}>
            <Gift {...icon} />{' '}
            {
              // i18n: menu item: the Rewards screen (daily bonus, levels, inviting friends)
              t('Rewards & referrals')
            }{' '}
            {chevron}
          </Link>
          <Link to="/support" onClick={onClose} className={row}>
            <LifeBuoy {...icon} /> {t('Help')} {chevron}
          </Link>
          <button type="button" onClick={onGuide} className={row}>
            <CirclePlay {...icon} />{' '}
            {
              // i18n: menu item that opens a short walkthrough of how the site works (steps, then a video)
              t('Guide me')
            }{' '}
            {chevron}
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
              auth.open('profile');
            }}
            className={row}
          >
            <UserPen {...icon} />{' '}
            {
              // i18n: menu item: change the name other people see
              t('Change name')
            }{' '}
            {chevron}
          </button>
          <LanguageButton
            className={row}
            render={(locale) => (
              <>
                <Languages {...icon} /> {t('Language')}
                <span className="ml-auto flex shrink-0 items-center gap-2 text-[14px] font-semibold text-graphite">
                  <LocaleFlag locale={locale} className="h-[18px] w-[18px]" />
                  {LOCALE_INFO[locale].native}
                </span>
                <ChevronRight size={18} aria-hidden="true" className="shrink-0 text-stone" />
              </>
            )}
          />
        </div>
        <div className={group}>
          <Link to="/terms" onClick={onClose} className={row}>
            <FileText {...icon} /> {t('Terms of Service')} {chevron}
          </Link>
          <Link to="/privacy" onClick={onClose} className={row}>
            <ShieldCheck {...icon} /> {t('Privacy Policy')} {chevron}
          </Link>
          <a href="mailto:support@gaintplay.com" className={row}>
            <Mail {...icon} />
            <span className="min-w-0 truncate">support@gaintplay.com</span>
          </a>
        </div>
        <div className={group}>
          <button type="button" onClick={logout} className={row.replace('text-ink', 'text-berry')}>
            <LogOut size={20} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />{' '}
            {t('Log out')}
          </button>
        </div>
        <p className="px-1 pt-1 text-center text-[12px] leading-relaxed text-stone">
          {
            // i18n: copyright line: {year} is the current year
            t('© {year} GaintPlay', { year: String(new Date().getFullYear()) })
          }{' '}
          · {t('Offers are provided by third-party partners.')}
        </p>
      </div>
    </BottomSheet>
  );
}

/* ------------------------------------------------------------------ Header (desktop, and signed-out phones) */

/** Desktop: a floating "sticker" bar (white, ink-outlined, offset shadow), the same
 *  pressed-on-paper language as the coin button. Its width and padding put the logo on the same
 *  left edge as page content (1200px container, 32px gutter). Phones (signed out): docked and
 *  full-bleed like the app bar. Signed in, phones get the AppBar instead. */
function Header({
  scrolled,
  pending,
  home,
}: {
  scrolled: boolean;
  pending: boolean;
  /** Where the logo leads. */
  home: string;
}) {
  const { data: me } = useMe();
  return (
    <header
      className={`site-header sticky top-0 z-30 max-lg:pt-safe max-lg:border-b max-lg:bg-canvas max-lg:transition-[border-color] lg:px-4 lg:pb-2 lg:transition-[padding] lg:duration-200 ${me ? 'max-lg:hidden' : ''} ${scrolled ? 'max-lg:border-hairline lg:pt-2' : 'max-lg:border-transparent lg:pt-4'}`}
    >
      <div
        className={`mx-auto flex h-[var(--app-bar-h)] items-center gap-3 px-4 lg:h-[60px] lg:max-w-[1168px] lg:gap-5 lg:rounded-[18px] lg:border-[1.5px] lg:border-ink lg:bg-paper lg:pl-4 lg:pr-2.5 lg:transition-shadow lg:duration-200 ${scrolled ? 'lg:shadow-[3px_3px_0_var(--color-ink)]' : 'lg:shadow-[5px_5px_0_var(--color-ink)]'}`}
      >
        <Logo to={home} />
        {me ? <AppNav /> : <MarketingNav />}
        {/* min-w-0 lets the optional bits shrink instead of pushing the avatar past the bar. */}
        <div className="ml-auto flex min-w-0 items-center gap-2">
          {me ? <UserControls me={me} /> : pending ? null : <GuestControls />}
        </div>
      </div>
    </header>
  );
}

/** Signed-out header links to the public guide pages (pre-rendered, see lib/seo.ts). */
const MARKETING_NAV: [string, string][] = [
  [msg('How it works'), '/how-it-works'],
  [msg('Ways to earn'), '/ways-to-earn'],
  [msg('Cash out'), '/gift-cards'],
  [msg('FAQ'), '/faq'],
];

function MarketingNav() {
  return (
    <nav aria-label={t('Main')} className="ml-2 hidden items-center gap-1 lg:flex">
      {MARKETING_NAV.map(([label, to]) => (
        <NavLink
          key={to}
          to={to}
          className="group relative rounded-full px-3.5 py-2 text-[14px] font-semibold text-graphite transition hover:text-ink aria-[current=page]:text-ink"
        >
          {t(label)}
          <span
            aria-hidden="true"
            className="absolute inset-x-3.5 bottom-1 h-[2px] origin-left scale-x-0 rounded-full bg-ink transition-transform duration-200 group-hover:scale-x-100 group-aria-[current=page]:scale-x-100"
          />
        </NavLink>
      ))}
    </nav>
  );
}

/** Segmented control: a sunken cream track with the active page lifted as a white sticker. */
function AppNav() {
  return (
    <nav
      aria-label={t('Main')}
      className="ml-2 hidden shrink-0 items-center gap-0.5 rounded-[14px] bg-canvas p-1 lg:flex"
    >
      {nav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          className={({ isActive }) =>
            `flex items-center gap-1.5 whitespace-nowrap rounded-[10px] px-2.5 py-1.5 text-[14px] font-semibold transition xl:px-3 ${
              isActive
                ? 'border-[1.5px] border-ink bg-paper text-ink shadow-[2px_2px_0_var(--color-ink)]'
                : 'border-[1.5px] border-transparent text-graphite hover:text-ink'
            }`
          }
        >
          <n.icon size={15} strokeWidth={2} className="hidden xl:block" aria-hidden="true" />
          {t(n.label)}
        </NavLink>
      ))}
    </nav>
  );
}

function GuestControls() {
  const { open } = useAuthDialog();
  const { data: stats } = useQuery(statsQuery<{ users: number; paidOutUsd: number }>());
  return (
    <>
      {stats && stats.users > 0 && (
        <span className="mr-2 hidden items-center gap-2 whitespace-nowrap text-[13px] font-semibold tabular-nums text-graphite xl:flex">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mint opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-mint" />
          </span>
          {
            // i18n: live count of people using GaintPlay, next to a pulsing green dot in the header
            t('{n, plural, one {# member earning} other {# members earning}}', { n: stats.users })
          }
        </span>
      )}
      <button
        onClick={() => open('login')}
        className="hidden whitespace-nowrap rounded-[10px] px-2 py-2.5 text-[14px] font-semibold text-ink transition hover:bg-canvas min-[360px]:block min-[400px]:px-3 min-[400px]:text-[15px] lg:px-3.5"
      >
        {t('Log in')}
      </button>
      <button
        onClick={() => open('signup')}
        className="btn-primary whitespace-nowrap px-3 py-2.5 max-[399px]:text-[14px] min-[400px]:px-4"
      >
        <span className="sm:hidden">
          {
            // i18n: header button on phones: keep it short
            t('Sign up')
          }
        </span>
        <span className="hidden sm:inline">{t('Sign up free')}</span>
      </button>
    </>
  );
}

function UserControls({ me }: { me: Me }) {
  // The streak link only shows from 2xl, so narrower screens (and phones, where this header is
  // hidden) don't ask for it.
  const wide = useMedia('(min-width: 96rem)');
  const { data: daily } = useQuery({ ...dailyQuery<Daily>(), enabled: wide });
  // i18n: tooltip of the header's daily bonus link: the user has claimed the bonus {day} days in a row
  const streakTitle = daily && t('Daily bonus streak: day {day}', { day: daily.day });
  return (
    <>
      {daily && (
        <Link
          to="/rewards"
          className="hidden min-h-10 min-w-0 shrink items-center gap-2 whitespace-nowrap rounded-[10px] px-3 py-2 text-[13px] font-bold transition hover:bg-canvas 2xl:flex"
          title={daily.claimedToday ? streakTitle : t('Claim today’s bonus')}
        >
          <Swatch color="sun" />
          {daily.claimedToday ? (
            <span className="truncate text-graphite">
              {
                // i18n: header link: the user has claimed the daily bonus {day} days in a row
                t('Day {day} streak', { day: daily.day })
              }
            </span>
          ) : (
            <span className="truncate">{t('Daily bonus')}</span>
          )}
        </Link>
      )}
      <Link
        to="/wallet"
        className="group flex min-h-10 shrink-0 items-center gap-2 rounded-[10px] bg-canvas py-1.5 pl-2 pr-3 transition hover:bg-coin-tint"
        // i18n: tooltip of the balance in the header, a link to the wallet
        title={t('Your balance: open your wallet')}
      >
        <CoinGlyph className="h-6 w-6" />
        <span className="leading-none">
          <span className="block text-[15px] font-extrabold tabular-nums text-ink">
            <span aria-hidden="true">{fmtCoins(me.balance.available)}</span>
            <span className="sr-only">
              {t('{n, plural, one {# coin} other {# coins}}', { n: me.balance.available })}
            </span>
          </span>
          {me.balance.pending > 0 && (
            <span className="mt-0.5 hidden whitespace-nowrap text-[11px] font-semibold tabular-nums text-stone sm:block">
              {
                // i18n: under the balance in the header: coins earned but not confirmed by the partner yet
                t('+{coins} pending', { coins: me.balance.pending })
              }
            </span>
          )}
        </span>
      </Link>
      <Link
        to="/cashout"
        className="btn-coin hidden shrink-0 whitespace-nowrap px-4 py-2 text-[14px] md:inline-flex"
      >
        {
          // i18n: header button that opens the Cash out screen (turn coins into gift cards)
          tc('action', 'Cash out')
        }
      </Link>
      <UserMenu me={me} />
    </>
  );
}

/** Desktop account menu (menu-button pattern): arrow keys move between items, Escape returns focus
 *  to the avatar, Tab moves on and closes it. Phones use the AccountSheet instead. */
function UserMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const focusFirst = useRef(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    if (focusFirst.current) {
      focusFirst.current = false;
      menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    }
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  const onMenuKey = (e: ReactKeyboardEvent) => {
    const items = Array.from(
      menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    const i = items.indexOf(document.activeElement as HTMLElement);
    const go = (n: number) => {
      e.preventDefault();
      items[(n + items.length) % items.length]?.focus();
    };
    if (e.key === 'ArrowDown') go(i + 1);
    else if (e.key === 'ArrowUp') go(i - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
    else if (e.key === 'Tab') setOpen(false);
  };
  const logout = async () => {
    // This browser stops getting the account's notifications (before the session goes).
    await stopBrowserPush();
    await api('/auth/logout', { body: {} }).catch(() => {});
    qc.clear();
    void navigate('/');
  };
  const name = me.user.displayName ?? me.user.username;
  const auth = useAuthDialog();
  const itemBase =
    'flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-left text-[14px] font-semibold text-graphite outline-offset-[-2px] hover:bg-canvas focus-visible:bg-canvas';
  const item = `${itemBase} hover:text-ink focus-visible:text-ink`;
  return (
    <div ref={ref} className="relative shrink-0">
      <button
        ref={trigger}
        onClick={(e) => {
          // A keyboard press (Enter/Space) reports detail 0: move focus into the menu as well.
          if (e.detail === 0) focusFirst.current = true;
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
          e.preventDefault();
          focusFirst.current = true;
          setOpen(true);
        }}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls="account-menu"
        // i18n: the avatar button that opens the account menu; {name} is the user's name
        aria-label={t('Account menu for {name}', { name })}
        className={`flex items-center gap-1 rounded-[10px] p-1 pr-1.5 transition hover:bg-canvas ${open ? 'bg-canvas' : ''}`}
      >
        <Avatar name={me.user.username} size={38} className="border-[1.5px] border-ink" />
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={`text-stone transition ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div
          ref={menu}
          id="account-menu"
          role="menu"
          aria-label={t('Account')}
          onKeyDown={onMenuKey}
          className="absolute right-0 top-[calc(100%+12px)] z-40 w-[272px] max-w-[calc(100vw-20px)] overflow-hidden rounded-[16px] border-[1.5px] border-ink bg-paper shadow-[4px_4px_0_var(--color-ink)]"
          style={{ animation: 'dialog-in 140ms ease-out' }}
        >
          <div className="border-b border-hairline px-4 py-4">
            <div className="flex items-center gap-3">
              <Avatar name={me.user.username} size={44} />
              <div className="min-w-0">
                <div className="truncate text-[15px] font-bold">{name}</div>
                <div className="truncate text-[13px] text-stone">{me.user.email}</div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[12px] font-semibold text-graphite">
              <span className="rounded-full bg-canvas px-2.5 py-1">
                {t('Level {level}', { level: me.user.level })}
              </span>
              <span className="rounded-full bg-canvas px-2.5 py-1">
                {
                  // i18n: a small badge with the user's referral code (like "Referral code AB12CD")
                  <T
                    msg="Referral code <b>{code}</b>"
                    values={{ code: me.user.referralCode }}
                    tags={{
                      b: (c) => <span className="font-bold tracking-wide text-ink">{c}</span>,
                    }}
                  />
                }
              </span>
            </div>
          </div>
          <div className="p-1.5">
            {(
              [
                [t('Wallet'), '/wallet', Wallet],
                [t('Account & privacy'), '/account', UserCog],
                [t('Rewards & referrals'), '/rewards', Gift],
                [t('Help'), '/support', LifeBuoy],
              ] as const
            ).map(([label, to, Icon]) => (
              <Link
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                role="menuitem"
                tabIndex={-1}
                className={item}
              >
                <Icon size={16} aria-hidden="true" /> {label}
              </Link>
            ))}
            <button
              onClick={() => {
                setOpen(false);
                auth.open('profile');
              }}
              role="menuitem"
              tabIndex={-1}
              className={item}
            >
              <UserPen size={16} aria-hidden="true" /> {t('Change name')}
            </button>
            <div role="separator" className="mx-3 my-1.5 h-px bg-hairline" />
            <button
              onClick={logout}
              role="menuitem"
              tabIndex={-1}
              className={`${itemBase} hover:text-berry focus-visible:text-berry`}
            >
              <LogOut size={16} aria-hidden="true" /> {t('Log out')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Language */

/** The language picker's code arrives when it is first opened. */
const LanguagePicker = lazyComponent(() =>
  import('./LanguagePicker').then((m) => m.LanguagePicker),
);

/**
 * Opens the language picker (components/LanguagePicker.tsx): the footer's on the website, a row in
 * the account sheet and on the Account screen in the app. `render` draws the button's inside for
 * the current language; its accessible name says what it does in any language.
 */
export function LanguageButton({
  className,
  render,
}: {
  className: string;
  render: (locale: ReturnType<typeof useLocale>) => ReactNode;
}) {
  const locale = useLocale();
  const [open, setOpen] = useState(false);
  // Nothing to choose until a second language is in the build.
  if (AVAILABLE.length < 2) return null;
  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        // i18n: the language button's name for screen readers, e.g. "Language: Deutsch"
        aria-label={t('Language: {name}', { name: LOCALE_INFO[locale].native })}
        onClick={() => setOpen(true)}
        onPointerEnter={() => void LanguagePicker.preload()}
        onFocus={() => void LanguagePicker.preload()}
        className={className}
      >
        {render(locale)}
      </button>
      {open && (
        <Suspense fallback={null}>
          <LanguagePicker onClose={() => setOpen(false)} />
        </Suspense>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ Footer */

/** Marketing footer: desktop, and signed-out visitors on phones in a browser. In the app (signed
 *  in, < lg) the tab bar and the account sheet's Terms/Privacy/Help links replace it; the installed
 *  app below lg never shows it, signed in or out (CSS, so pre-rendered pages don't flash it). */
function Footer({ onGuide }: { onGuide: () => void }) {
  const { data: me } = useMe();
  const { open } = useAuthDialog();
  const cta =
    'btn-coin self-start border-white shadow-[3px_3px_0_#fff] focus-visible:outline-white md:self-end';
  return (
    <footer className="relative mt-auto overflow-hidden bg-ink text-white app:hidden max-lg:standalone:hidden">
      <div className="mx-auto max-w-[1200px] px-(--gutter) pt-16 md:pt-20">
        {/* Statement + action */}
        <div className="flex flex-col gap-8 border-b border-white/10 pb-12 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="heading-lg max-w-[13ch] text-white">
              {
                // i18n: the footer's slogan, three short steps; <coin> (the last) is orange and kept on one line
                <T
                  msg="Play. Earn. <coin>Cash out.</coin>"
                  tags={{ coin: (c) => <span className="whitespace-nowrap text-coin">{c}</span> }}
                />
              }
            </h2>
            <p className="mt-4 max-w-md text-pretty text-[16px] leading-relaxed text-white/65">
              {
                // i18n: write 1,000 and $1 the way your language writes numbers; amounts stay in US dollars
                t(
                  'Games, apps and surveys from trusted partners. 1,000 coins = $1, gift cards from $1.',
                )
              }
            </p>
          </div>
          {me ? (
            <Link to="/earn" className={cta}>
              {t('Find something to earn')}
            </Link>
          ) : (
            <button onClick={() => open('signup')} className={cta}>
              {t('Start earning — it’s free')}
            </button>
          )}
        </div>

        {/* Columns + trust */}
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 py-12 md:grid-cols-[1fr_1fr_1fr_1.3fr] md:gap-x-10">
          <FooterCol
            // i18n: heading of a footer column of links to the guide pages
            title={t('Learn')}
            links={[
              // i18n: footer link that opens a short walkthrough of how the site works (steps, then a video)
              [t('Guide me'), onGuide],
              [t('How it works'), '/how-it-works'],
              [t('Ways to earn'), '/ways-to-earn'],
              [t('Gift cards'), '/gift-cards'],
              [t('FAQ'), '/faq'],
            ]}
          />
          <FooterCol
            title={t('Earn')}
            links={[
              // i18n: footer link: the partners' offer walls, where people earn coins
              [t('Offer walls'), '/earn'],
              [t('Daily bonus'), '/rewards'],
              [t('Leaderboard'), '/leaderboard'],
              [t('Invite friends'), '/rewards'],
            ]}
          />
          <FooterCol
            title={t('Help')}
            links={[
              // i18n: footer link to the Help screen
              [t('Help centre'), '/support'],
              // i18n: footer link to the Terms of Service
              [t('Terms'), '/terms'],
              // i18n: footer link to the Privacy Policy
              [t('Privacy'), '/privacy'],
            ]}
          />
          <ul className="col-span-2 space-y-3 md:col-span-1">
            {(
              [
                [
                  CoinsIcon,
                  // i18n: footer promise: people can cash out amounts as small as $1 (write $1 your language's way)
                  t('Cash out from $1'),
                  // i18n: unlike sites that make people earn $20 before they can cash out
                  t('No $20 minimums.'),
                ],
                [
                  Mail,
                  t('Gift cards by email'),
                  // i18n: what happens to a cash-out: our team checks it, then the gift card is sent
                  t('Reviewed, then delivered.'),
                ],
                [ShieldCheck, t('One account per person'), t('Keeps payouts fair for everyone.')],
              ] as const
            ).map(([Icon, title, about]) => (
              <li key={title} className="flex gap-3 rounded-[14px] border border-white/10 p-3.5">
                <Icon
                  size={18}
                  strokeWidth={1.75}
                  aria-hidden="true"
                  className="mt-px shrink-0 text-white/70"
                />
                <div>
                  <div className="text-[14px] font-bold leading-snug">{title}</div>
                  <div className="mt-0.5 text-[13px] text-white/60">{about}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Oversized wordmark, cropped by the legal bar below it, with the coin sitting on it */}
      <div className="relative mx-auto max-w-[1400px] select-none px-3" aria-hidden="true">
        <div className="text-center text-[22vw] font-extrabold leading-[0.78] tracking-[-0.06em] text-white/[0.07] md:text-[17vw] xl:text-[15.5rem]">
          GaintPlay
        </div>
        <CoinMascot
          size={140}
          rotate={-16}
          className="absolute right-[6%] top-[-10%] hidden md:block"
        />
      </div>
      <div className="pb-safe relative border-t border-white/10 bg-ink">
        <div className="mx-auto flex max-w-[1200px] flex-col gap-1 px-(--gutter) py-5 text-[12px] text-white/55 md:flex-row md:flex-wrap md:items-center md:gap-x-6">
          <span>{t('© {year} GaintPlay', { year: String(new Date().getFullYear()) })}</span>
          <span>{t('Offers are provided by third-party partners. Rewards vary by country.')}</span>
          <LanguageButton
            className="-mx-2 mt-2 inline-flex min-h-10 items-center gap-2 self-start rounded-full border border-white/20 px-3 text-[13px] font-semibold text-white/80 transition hover:border-white/50 hover:text-white md:my-0 md:ml-auto md:min-h-9"
            render={(locale) => (
              <>
                <LocaleFlag locale={locale} className="h-[18px] w-[18px]" />
                {LOCALE_INFO[locale].native}
                <ChevronDown size={14} aria-hidden="true" className="text-white/55" />
              </>
            )}
          />
        </div>
      </div>
    </footer>
  );
}

/** A column of links; a function instead of an address is a button (opens something in place). */
function FooterCol({ title, links }: { title: string; links: [string, string | (() => void)][] }) {
  const cls =
    '-mx-2 inline-flex min-h-10 items-center rounded-[8px] px-2 text-white/80 transition hover:text-white md:min-h-0 md:py-1';
  return (
    <div>
      <h3 className="flex items-center gap-2 text-[13px] font-semibold text-white/55">
        <Swatch color="coin" /> {title}
      </h3>
      <ul className="mt-3 text-[15px] md:mt-4 md:space-y-1.5">
        {links.map(([label, href]) => (
          <li key={label}>
            {typeof href === 'function' ? (
              <button type="button" onClick={href} className={cls}>
                {label}
              </button>
            ) : href.includes('#') ? (
              <a href={href} className={cls}>
                {label}
              </a>
            ) : (
              <Link to={href} className={cls}>
                {label}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ------------------------------------------------------------------ Verify banner */

/** The API sent a link less than a minute ago (at sign-up): no second email. Marked: show it with
 *  t(RESEND_RECENT). */
// i18n: the user asked for the link that confirms their email address again, just after signing up
export const RESEND_RECENT = msg(
  'We sent the link a moment ago. Check your inbox and spam; it can take a minute to arrive.',
);

function VerifyBanner() {
  const [state, setState] = useState<{ ok: boolean; text: string } | null>(null);
  const sending = useRef(false);
  const resend = async () => {
    if (sending.current) return; // a double tap sends one email
    sending.current = true;
    try {
      const r = await api<{ recent?: boolean }>('/auth/verify-email/resend', { body: {} });
      // i18n: after the user asked us to send the link that confirms their email address again
      const sent = t('Sent. Check your inbox (and spam) for the link.');
      setState({
        ok: true,
        text: r.recent ? t(RESEND_RECENT) : sent,
      });
    } catch (e) {
      setState({ ok: false, text: (e as Error).message });
    } finally {
      sending.current = false;
    }
  };
  // Sits on the content grid (same 1200px container and gutters as the page below it).
  return (
    <div className="mx-auto w-full max-w-[1200px] px-(--gutter) pt-3 lg:pt-2">
      <div
        role="status"
        className="flex items-center gap-x-3 rounded-[14px] bg-coin-tint px-4 py-2.5 text-[14px] text-ink lg:justify-center"
      >
        <MailWarning size={18} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />
        {state ? (
          <span className={`font-semibold ${state.ok ? 'text-mint-deep' : 'text-berry'}`}>
            {state.text}
          </span>
        ) : (
          <p className="min-w-0 text-pretty leading-snug">
            {t('Confirm your email to cash out and claim bonuses.')}
            {gap()}
            <button
              onClick={resend}
              className="-my-2.5 whitespace-nowrap rounded-[6px] py-2.5 font-bold underline underline-offset-2 hover:decoration-2"
            >
              {
                // i18n: button: email the link that confirms the user's email address again
                t('Resend link')
              }
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * "+50 coins": the moment the Android app bonus lands (lib/appDevice.ts fires APP_BONUS_EVENT when
 * the device report comes back with it). A sticker that drops in at the top for a few seconds.
 */
function AppBonusSticker() {
  const qc = useQueryClient();
  const [coins, setCoins] = useState<number | null>(null);
  useEffect(() => {
    let timer = 0;
    const on = (e: Event) => {
      setCoins((e as CustomEvent<number>).detail);
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['wallet'] });
      void qc.invalidateQueries({ queryKey: ['reward-extras'] });
      navigator.vibrate?.([10, 40, 18]);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setCoins(null), 4200);
    };
    window.addEventListener(APP_BONUS_EVENT, on);
    return () => {
      window.removeEventListener(APP_BONUS_EVENT, on);
      window.clearTimeout(timer);
    };
  }, [qc]);
  if (coins === null) return null;
  return (
    <div
      role="status"
      className="pointer-events-none fixed inset-x-0 top-[calc(var(--safe-top)+12px)] z-50 flex justify-center px-4"
    >
      <div
        className="sticker flex items-center gap-2.5 rounded-full border-[1.5px] border-ink bg-paper py-1.5 pl-1.5 pr-4 text-[15px] font-semibold text-ink"
        style={{ animation: 'notif-drop 460ms var(--ease-stack) backwards' }}
      >
        <CoinMascot size={30} rotate={-10} />
        <span>
          {
            // i18n: a short pop-up when the Android app's bonus lands: "+50 coins for using the app"
            <T
              msg="{n, plural, one {<b>+#</b> coin} other {<b>+#</b> coins}} for using the app"
              values={{ n: coins }}
              tags={{
                b: (c) => <b className="font-extrabold tabular-nums text-coin-ink">{c}</b>,
              }}
            />
          }
        </span>
      </div>
    </div>
  );
}

/** localStorage: the announcement text this browser closed. A different message shows again. */
const ANNOUNCEMENT_CLOSED = 'gp_announcement_closed';

/**
 * Admin → Settings → Site → Announcement banner, on every page, signed in or out. The text comes
 * from /api/public/stats (cached up to a minute at the edge) and is shown as plain text.
 */
function Announcement() {
  const { data } = useQuery(statsQuery<{ announcement: string | null }>());
  const text = data?.announcement?.trim();
  const [closed, setClosed] = useState(() => {
    try {
      return localStorage.getItem(ANNOUNCEMENT_CLOSED);
    } catch {
      return null;
    }
  });
  if (!text || closed === text) return null;
  const close = () => {
    setClosed(text);
    try {
      localStorage.setItem(ANNOUNCEMENT_CLOSED, text);
    } catch {
      /* storage blocked: closed until the next page load */
    }
  };
  // A sun-yellow sticker on the content grid (like VerifyBanner below it): the one loud note on
  // the page, so it can't be mistaken for part of the header or the live band.
  return (
    <div className="mx-auto w-full max-w-[1200px] px-(--gutter) pt-4 lg:pt-5">
      <div
        role="status"
        className="sticker flex items-center gap-x-3 rounded-[14px] bg-sun py-1.5 pl-3 pr-1.5 text-[15px] text-ink"
      >
        <span
          aria-hidden="true"
          className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink text-sun"
        >
          <Megaphone size={16} strokeWidth={2} />
        </span>
        <p className="min-w-0 flex-1 py-1 text-pretty font-semibold leading-snug [overflow-wrap:anywhere] lg:text-center">
          {text}
        </p>
        <button
          type="button"
          onClick={close}
          aria-label={t('Close announcement')}
          className="pressable grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-ink hover:bg-ink/10"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
